import React, { useEffect, useState } from "react";
import { ContactsService } from "../../services/contactsService";
import { ContactListWithCount } from "../../types/contactList";

interface WorkflowConfig {
  name: string;
  contactListId: string;
  description: string;
  mode: "manual" | "automatic";
}

interface WorkflowConfigPanelProps {
  config: WorkflowConfig;
  onChange: (config: WorkflowConfig) => void;
  onClose: () => void;
}

/**
 * WorkflowConfigPanel - Matches the screenshot design
 * - Header with title + modern pill toggle
 * - Two-column layout for name + contact list
 * - Full-width description
 */
const WorkflowConfigPanel: React.FC<WorkflowConfigPanelProps> = ({
  config,
  onChange,
  onClose,
}) => {
  const [contactLists, setContactLists] = useState<ContactListWithCount[]>([]);
  const [isLoadingLists, setIsLoadingLists] = useState(false);

  useEffect(() => {
    loadContactLists();
  }, []);

  const loadContactLists = async () => {
    setIsLoadingLists(true);
    try {
      const lists = await ContactsService.getContactLists();
      setContactLists(lists);
    } catch (error) {
      console.error("Failed to load contact lists:", error);
    } finally {
      setIsLoadingLists(false);
    }
  };

  const handleChange = (field: keyof WorkflowConfig, value: string) => {
    onChange({
      ...config,
      [field]: value,
    });
  };

  const handleModeToggle = () => {
    onChange({
      ...config,
      mode: config.mode === "manual" ? "automatic" : "manual",
    });
  };

  const isAutomatic = config.mode === "automatic";

  return (
    <div className="su-section su-workflow-config-panel">
      {/* Header Row - Title + Toggle */}
      <div className="su-workflow-config-header">
        <h2 className="su-workflow-config-title">Workflow Configuration</h2>
        
        {/* Modern Pill Toggle */}
        <div className="su-mode-toggle-container">
          <span 
            className={`su-mode-label ${!isAutomatic ? "active" : ""}`}
            onClick={() => !isAutomatic || handleModeToggle()}
          >
            MANUAL
          </span>
          
          <button
            type="button"
            className="su-pill-toggle"
            onClick={handleModeToggle}
            aria-label="Toggle workflow mode"
            role="switch"
            aria-checked={isAutomatic}
          >
            <span 
              className="su-pill-toggle-thumb"
              style={{
                transform: isAutomatic ? "translateX(24px)" : "translateX(0)",
              }}
            />
          </button>
          
          <span 
            className={`su-mode-label ${isAutomatic ? "active" : ""}`}
            onClick={() => isAutomatic || handleModeToggle()}
          >
            AUTOMATIC
          </span>
        </div>
      </div>

      {/* Row 1: Workflow Name + Contact List */}
      <div className="su-workflow-config-row">
        <div className="su-workflow-config-field">
          <label className="su-workflow-config-label">Workflow Name</label>
          <input
            type="text"
            className="su-workflow-config-input"
            placeholder="Weekly Investor Update"
            value={config.name}
            onChange={(e) => handleChange("name", e.target.value)}
          />
        </div>

        <div className="su-workflow-config-field">
          <label className="su-workflow-config-label">Contact List</label>
          <select
            className="su-workflow-config-select"
            value={config.contactListId}
            onChange={(e) => handleChange("contactListId", e.target.value)}
            disabled={isLoadingLists}
          >
            <option value="">Select contact list...</option>
            {contactLists.map((list) => (
              <option key={list.id} value={list.id}>
                {list.name} ({list.contact_count} contacts)
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Row 2: Full-width Description */}
      <div className="su-workflow-config-field su-workflow-config-field-full">
        <label className="su-workflow-config-label">Description</label>
        <textarea
          className="su-workflow-config-textarea"
          placeholder="Generate insights from GitHub commits, Slack discussions, and ClickUp tasks to create comprehensive investor updates."
          value={config.description}
          onChange={(e) => handleChange("description", e.target.value)}
          rows={2}
        />
      </div>
    </div>
  );
};

export default WorkflowConfigPanel;
